import type { ConnectionDto, EmailCadence, ResetAskedDto } from '@nsb/core';
import { type ChangeEvent, type FormEvent, type ReactNode, useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText, queryClient, setViewAs, useAction, useApi } from '../api.ts';
import { useFeedbackRecord } from '../components/feedback-context.ts';
import { Button, ErrorLine, Field, Page, Section, TextInput, Title } from '../components/ui.tsx';
import { useUnsaved } from '../components/useUnsaved.ts';
import { useMe } from '../session.tsx';
import { sameValue } from '../unsaved.ts';
import { ClaudeConnections } from './ConnectClaude.tsx';
import { PASSWORD_MIN, PASSWORD_RULE, passwordRefusal } from './passwordForm.ts';
import { resetSentWords } from './resetWords.ts';

/** How often a client is emailed about what waits on them (spec "Client Emails" §6): gathered unless they choose. */
const CADENCES: { value: EmailCadence; label: string; hint: string | null }[] = [
  { value: 'each', label: 'Each time, at once', hint: null },
  { value: 'gathered', label: 'Gathered, at most one a day', hint: '15 minutes after the first new thing, with everything new by then' },
  { value: 'morning', label: 'A morning note at 8 am', hint: 'When something new is waiting' },
];

/** What the API answers when a password is set or changed: how many other devices it signed out. */
interface PasswordSaved {
  ok: true;
  signedOut: number;
}

export function Account() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [name, setName] = useState(me?.user.name ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useFeedbackRecord(null, null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const saveName = () =>
    void run(async () => {
      await api('/me', { method: 'PATCH', body: { name } });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    });
  // The name changed and not saved, on the bar at the window's foot, which saves as Save beside it does (P-29).
  useUnsaved({
    names: me && !sameValue(name, me.user.name) ? ['Name'] : [],
    busy,
    error: err,
    onSave: saveName,
    onDiscard: () => (setName(me?.user.name ?? ''), setErr(null)),
  });
  if (!me) return null;
  return (
    <Page>
      <Title kicker="Account">{me.user.name ?? me.user.email}</Title>
      <p className="text-ink-2">{me.user.email}</p>
      <Section title="Name">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-64 flex-1">
            <TextInput aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button tone="quiet" onClick={saveName}>
            Save
          </Button>
        </div>
      </Section>
      <Section title="Password">
        <AccountPassword email={me.user.email} hasPassword={me.user.hasPassword} />
      </Section>
      {me.org ? (
        <Section title="Claude">
          <AccountClaude />
        </Section>
      ) : null}
      {me.projects.some((p) => p.role === 'client') ? (
        <Section title="Emails">
          <p className="mb-3 text-sm text-ink-3">When something on your project waits on you.</p>
          <fieldset className="border-y border-rule">
            <legend className="sr-only">How often you are emailed</legend>
            {CADENCES.map((c) => (
              <label key={c.value} className="flex min-h-11 cursor-pointer items-baseline gap-3 border-b border-rule py-3 last:border-b-0">
                <input
                  type="radio"
                  name="email-cadence"
                  className="relative top-0.5 size-4 shrink-0 accent-moss"
                  checked={me.user.emailCadence === c.value}
                  disabled={busy}
                  onChange={() =>
                    void run(async () => {
                      await api('/me', { method: 'PATCH', body: { emailCadence: c.value } });
                      await queryClient.invalidateQueries({ queryKey: ['me'] });
                    })
                  }
                />
                <span>
                  <span className="block">{c.label}</span>
                  {c.hint ? <span className="block text-sm text-ink-3">{c.hint}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
        </Section>
      ) : null}
      <ErrorLine error={err} />
      <div className="mt-12 border-t border-rule pt-6">
        <Button
          tone="quiet"
          onClick={() =>
            void run(async () => {
              await api('/auth/logout', { body: {} });
              setViewAs(null);
              queryClient.clear();
              navigate('/login');
            })
          }
        >
          Sign out
        </Button>
      </div>
    </Page>
  );
}

const CLAUDE_CONNECTIONS = ['claude-connections'];

/** Account → Claude, for staff: the connections of Claude to this person (spec "Claude Connector" §3.5), each ended with Revoke. */
function AccountClaude() {
  const { data, error } = useApi<ConnectionDto[]>(CLAUDE_CONNECTIONS, '/me/connections');
  const revoke = useAction((id: string) => api(`/me/connections/${id}/revoke`, { body: {} }), { invalidate: [CLAUDE_CONNECTIONS] });
  return (
    <>
      {data ? <ClaudeConnections connections={data} origin={window.location.origin} busy={revoke.isPending} onRevoke={(id) => revoke.mutate(id)} /> : null}
      <ErrorLine error={errorText(error) ?? errorText(revoke.error)} />
    </>
  );
}

/**
 * Account → Password, as every app has it (L-13, Jason, 2026-10-07): with none, Set a password; with one, Change
 * password, asking for the current one, and a link emailed to choose a new one for whoever has forgotten it (the same
 * link as Forgot your password on the sign-in page, to the page that asks for it, ResetPassword.tsx). Never removed.
 * Either signs out the other devices, and says so.
 */
export function AccountPassword({ email, hasPassword }: { email: string; hasPassword: boolean }) {
  const [signedOut, setSignedOut] = useState<number | null>(null);
  const [reset, setReset] = useState<ResetAskedDto | null>(null);
  const [resetErr, setResetErr] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const forgot = async () => {
    setResetting(true);
    setResetErr(null);
    setSignedOut(null);
    try {
      setReset(await api<ResetAskedDto>('/auth/reset', { body: { email, redirect: '/account' } }));
    } catch (e) {
      setResetErr(errorText(e));
    } finally {
      setResetting(false);
    }
  };
  return (
    <>
      <p className="mb-5 text-ink-2">
        {hasPassword
          ? 'Changing it signs you out on your other devices.'
          : 'You have no password yet: you sign in with a link by email. Setting one signs you out on your other devices.'}
      </p>
      <PasswordForm
        // A new form once a password is set: the fields start empty, and the current one is asked for from then on.
        key={hasPassword ? 'change' : 'set'}
        email={email}
        asksCurrent={hasPassword}
        submitWords={hasPassword ? 'Change password' : 'Set a password'}
        save={async (password, current) => {
          setSignedOut(null);
          setReset(null);
          const r = await api<PasswordSaved>('/me/password', { body: current === null ? { password } : { password, current }, notice: hasPassword ? 'Password changed' : 'Password set' });
          await queryClient.invalidateQueries({ queryKey: ['me'] });
          setSignedOut(r.signedOut);
        }}
        aside={
          hasPassword ? (
            // What it does, in its words: pressed, the link goes out at once.
            <Button tone="quiet" busy={resetting} onClick={() => void forgot()}>
              Forgot it? Email me a link
            </Button>
          ) : null
        }
      />
      {signedOut ? (
        <p className="mt-4 text-sm text-ink-2">
          Signed out on your {signedOut === 1 ? 'other device' : `${signedOut} other devices`}.
        </p>
      ) : null}
      {reset ? (
        <div className="mt-4">
          <p>{resetSentWords(reset, email).line}</p>
          <p className="meta mt-1">{resetSentWords(reset, email).meta}</p>
          {reset.devLink ? (
            <p className="mt-2 text-sm text-ink-3">
              Development: <a className="link" href={reset.devLink}>open the link</a>
            </p>
          ) : null}
        </div>
      ) : null}
      <ErrorLine error={resetErr} />
    </>
  );
}

/**
 * A password chosen, the one form for Account and for choosing one on the way in: the account's email hidden for
 * password managers, the current one where it is asked for, the new one twice with Show passwords, the rule shown
 * before typing. Pressed, it sends or says in words why not (`passwordRefusal`); the API's refusal (the current one
 * wrong) shows the same way.
 */
export function PasswordForm({
  email,
  asksCurrent,
  submitWords,
  save,
  aside,
}: {
  email: string;
  asksCurrent: boolean;
  submitWords: string;
  save: (password: string, current: string | null) => Promise<void>;
  /** Beside the button: Forgot your current password, or Sign out. */
  aside?: ReactNode;
}) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ruleId = useId();
  const type = show ? 'text' : 'password';
  const edit = (set: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    set(e.target.value);
    setErr(null);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const asked = asksCurrent ? current : null;
    const refusal = passwordRefusal({ current: asked, password, again });
    if (refusal) {
      setErr(refusal);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await save(password, asked);
      setCurrent('');
      setPassword('');
      setAgain('');
      setShow(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    // The words are the form's own: no browser bubbles over them.
    <form onSubmit={(e) => void submit(e)} noValidate className="max-w-md">
      {/* Whose password this is, for a password manager to save it under. */}
      <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
      {asksCurrent ? (
        <Field label="Current password">
          {(id) => <TextInput id={id} name="current-password" type={type} autoComplete="current-password" value={current} onChange={edit(setCurrent)} />}
        </Field>
      ) : null}
      {/* The rule read out with the field, not only seen under it. */}
      <Field label="New password" hint={<span id={ruleId}>{PASSWORD_RULE}</span>}>
        {(id) => (
          <TextInput
            id={id}
            name="new-password"
            type={type}
            autoComplete="new-password"
            minLength={PASSWORD_MIN}
            aria-describedby={ruleId}
            value={password}
            onChange={edit(setPassword)}
          />
        )}
      </Field>
      <Field label="Confirm new password">
        {(id) => <TextInput id={id} name="confirm-password" type={type} autoComplete="new-password" minLength={PASSWORD_MIN} value={again} onChange={edit(setAgain)} />}
      </Field>
      <label className="-mt-2 flex min-h-11 cursor-pointer items-center gap-3 text-ink-2">
        <input type="checkbox" className="size-4 shrink-0 accent-moss" checked={show} onChange={(e) => setShow(e.target.checked)} />
        Show passwords
      </label>
      <ErrorLine error={err} />
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-1">
        <Button type="submit" busy={busy}>
          {submitWords}
        </Button>
        {aside}
      </div>
    </form>
  );
}

/**
 * Before anything else, someone signed in with a temporary password NSB set,
 * or by an invitation while they have no password yet, chooses their own (the
 * API refuses everything else until they do). A forgotten password is chosen
 * on its link's own page instead (ResetPassword.tsx), never held here.
 */
export function ChoosePassword() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const first = me?.user.name?.split(/\s+/)[0] ?? null;
  const signOut = async () => {
    await api('/auth/logout', { body: {} }).catch(() => undefined);
    setViewAs(null);
    queryClient.clear();
    navigate('/login');
  };
  return (
    <Page>
      <div className="max-w-sm pt-6 sm:pt-12">
        {me?.user.hasPassword ? (
          <>
            <Title kicker="Your password">Choose a new password</Title>
            <p className="mb-8 text-ink-2">
              {first ? `${first}, choose` : 'Choose'} a new password to carry on. You sign in with it from now on, with {me.user.email}.
            </p>
          </>
        ) : (
          <>
            <Title kicker="Welcome">Choose your password</Title>
            <p className="mb-8 text-ink-2">
              {first ? `Welcome, ${first}. Choose` : 'Welcome. Choose'} a password to see your project. You sign in with it from now on, with {me?.user.email ?? 'your email'}; a sign-in link by email works too, whenever you need one.
            </p>
          </>
        )}
        <PasswordForm
          email={me?.user.email ?? ''}
          asksCurrent={false}
          submitWords="Save and continue"
          save={async (password) => {
            await api('/me/password', { body: { password }, notice: me?.user.hasPassword ? 'Password changed' : 'Password set' });
            await queryClient.invalidateQueries();
          }}
          aside={
            <Button tone="quiet" onClick={() => void signOut()}>
              Sign out
            </Button>
          }
        />
      </div>
    </Page>
  );
}
